/**
 * TOL 2008 (Toimialaluokitus) Category Mappings
 * 
 * Finnish Standard Industrial Classification (TOL) based on EU NACE Rev. 2
 * This file contains hierarchical mapping of industry codes used for filtering
 * company profiles by business category.
 * 
 * Structure:
 * - Level 1: Letter sections (A-U, e.g., "F Rakentaminen")
 * - Level 2: Main category (2 digits, e.g., "41-43" under F)
 * - Level 5: Detailed sub-category (5 digits, e.g., "41200")
 * 
 * Data imported from Excel includes both levels in publicData:
 * - industryCodeLevel2
 * - industryCodeLevel5
 */

// Import Level 5 categories from generated JSON
import level5CategoriesData from './level5-categories.json';

// Level 1 - Main sections (Letters A-U)
// These group the 88 Level 2 categories for better UX
export const TOL_LEVEL_1_SECTIONS = [
  { code: 'A', label: 'Maatalous, metsätalous ja kalatalous', codes: ['01', '02', '03'] },
  { code: 'B', label: 'Kaivostoiminta ja louhinta', codes: ['05', '06', '07', '08', '09'] },
  { code: 'C', label: 'Teollisuus', codes: ['10', '11', '12', '13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24', '25', '26', '27', '28', '29', '30', '31', '32', '33'] },
  { code: 'D', label: 'Sähkö-, kaasu- ja lämpöhuolto, jäähdytysliiketoiminta', codes: ['35'] },
  { code: 'E', label: 'Vesihuolto, viemäri- ja jätevesihuolto, jätehuolto ja muu ympäristön puhtaanapito', codes: ['36', '37', '38', '39'] },
  { code: 'F', label: 'Rakentaminen', codes: ['41', '42', '43'] },
  { code: 'G', label: 'Tukku- ja vähittäiskauppa; moottoriajoneuvojen ja moottoripyörien korjaus', codes: ['45', '46', '47'] },
  { code: 'H', label: 'Kuljetus ja varastointi', codes: ['49', '50', '51', '52', '53'] },
  { code: 'I', label: 'Majoitus- ja ravitsemistoiminta', codes: ['55', '56'] },
  { code: 'J', label: 'Informaatio ja viestintä', codes: ['58', '59', '60', '61', '62', '63'] },
  { code: 'K', label: 'Rahoitus- ja vakuutustoiminta', codes: ['64', '65', '66'] },
  { code: 'L', label: 'Kiinteistöalan toiminta', codes: ['68'] },
  { code: 'M', label: 'Ammatillinen, tieteellinen ja tekninen toiminta', codes: ['69', '70', '71', '72', '73', '74', '75'] },
  { code: 'N', label: 'Hallinto- ja tukipalvelutoiminta', codes: ['77', '78', '79', '80', '81', '82'] },
  { code: 'O', label: 'Julkinen hallinto ja maanpuolustus; pakollinen sosiaalivakuutus', codes: ['84'] },
  { code: 'P', label: 'Koulutus', codes: ['85'] },
  { code: 'Q', label: 'Terveys- ja sosiaalipalvelut', codes: ['86', '87', '88'] },
  { code: 'R', label: 'Taiteet, viihde ja virkistys', codes: ['90', '91', '92', '93'] },
  { code: 'S', label: 'Muu palvelutoiminta', codes: ['94', '95', '96'] },
  { code: 'T', label: 'Kotitalouksien toiminta työnantajina; kotitalouksien eriyttämätön toiminta tavaroiden ja palvelujen tuottamiseksi omaan käyttöön', codes: ['97'] },
  { code: 'U', label: 'Kansainvälisten organisaatioiden ja toimielinten toiminta', codes: ['99'] },
];

// Level 5 categories (subcategories) - imported from Excel data
// 577 unique subcategories with parent Level 2 relationships
export const TOL_LEVEL_5_CATEGORIES = level5CategoriesData;

// Level 2 categories (main categories)
export const TOL_LEVEL_2_CATEGORIES = [
  { code: '01', label: 'Kasvinviljely ja kotieläintalous, riistatalous ja niihin liittyvät palvelut', labelEn: 'Crop and animal production, hunting and related services' },
  { code: '02', label: 'Metsätalous ja puunkorjuu', labelEn: 'Forestry and logging' },
  { code: '03', label: 'Kalatalous', labelEn: 'Fishing and aquaculture' },
  { code: '05', label: 'Kivihiilen ja ruskohiilen kaivu', labelEn: 'Mining of coal and lignite' },
  { code: '06', label: 'Raakaöljyn ja maakaasun tuotanto', labelEn: 'Extraction of crude petroleum and natural gas' },
  { code: '07', label: 'Metallimalmien louhinta', labelEn: 'Mining of metal ores' },
  { code: '08', label: 'Muu kaivostoiminta ja louhinta', labelEn: 'Other mining and quarrying' },
  { code: '09', label: 'Kaivostoimintaa palveleva toiminta', labelEn: 'Mining support service activities' },
  { code: '10', label: 'Elintarvikkeiden valmistus', labelEn: 'Manufacture of food products' },
  { code: '11', label: 'Juomien valmistus', labelEn: 'Manufacture of beverages' },
  { code: '12', label: 'Tupakkatuotteiden valmistus', labelEn: 'Manufacture of tobacco products' },
  { code: '13', label: 'Tekstiilien valmistus', labelEn: 'Manufacture of textiles' },
  { code: '14', label: 'Vaatteiden valmistus', labelEn: 'Manufacture of wearing apparel' },
  { code: '15', label: 'Nahan ja nahkatuotteiden valmistus', labelEn: 'Manufacture of leather and related products' },
  { code: '16', label: 'Sahatavaran sekä puu- ja korkkituotteiden valmistus', labelEn: 'Manufacture of wood and products of wood' },
  { code: '17', label: 'Paperin, paperi- ja kartonkituotteiden valmistus', labelEn: 'Manufacture of paper and paper products' },
  { code: '18', label: 'Painaminen ja tallenteiden jäljentäminen', labelEn: 'Printing and reproduction of recorded media' },
  { code: '19', label: 'Koksin ja jalostettujen öljytuotteiden valmistus', labelEn: 'Manufacture of coke and refined petroleum products' },
  { code: '20', label: 'Kemikaalien ja kemiallisten tuotteiden valmistus', labelEn: 'Manufacture of chemicals and chemical products' },
  { code: '21', label: 'Lääkeaineiden ja lääkkeiden valmistus', labelEn: 'Manufacture of basic pharmaceutical products' },
  { code: '22', label: 'Kumi- ja muovituotteiden valmistus', labelEn: 'Manufacture of rubber and plastic products' },
  { code: '23', label: 'Muiden ei-metallisten mineraalituotteiden valmistus', labelEn: 'Manufacture of other non-metallic mineral products' },
  { code: '24', label: 'Metallien jalostus', labelEn: 'Manufacture of basic metals' },
  { code: '25', label: 'Metallituotteiden valmistus', labelEn: 'Manufacture of fabricated metal products' },
  { code: '26', label: 'Tietokoneiden sekä elektronisten ja optisten tuotteiden valmistus', labelEn: 'Manufacture of computer, electronic and optical products' },
  { code: '27', label: 'Sähkölaitteiden valmistus', labelEn: 'Manufacture of electrical equipment' },
  { code: '28', label: 'Muiden koneiden ja laitteiden valmistus', labelEn: 'Manufacture of machinery and equipment n.e.c.' },
  { code: '29', label: 'Moottoriajoneuvojen valmistus', labelEn: 'Manufacture of motor vehicles, trailers and semi-trailers' },
  { code: '30', label: 'Muiden kulkuneuvojen valmistus', labelEn: 'Manufacture of other transport equipment' },
  { code: '31', label: 'Huonekalujen valmistus', labelEn: 'Manufacture of furniture' },
  { code: '32', label: 'Muu valmistus', labelEn: 'Other manufacturing' },
  { code: '33', label: 'Koneiden ja laitteiden korjaus, huolto ja asennus', labelEn: 'Repair and installation of machinery and equipment' },
  { code: '35', label: 'Sähkö-, kaasu- ja lämpöhuolto, jäähdytysliiketoiminta', labelEn: 'Electricity, gas, steam and air conditioning supply' },
  { code: '36', label: 'Veden otto, puhdistus ja jakelu', labelEn: 'Water collection, treatment and supply' },
  { code: '37', label: 'Viemäri- ja jätevesihuolto', labelEn: 'Sewerage' },
  { code: '38', label: 'Jätteen keruu, käsittely ja loppusijoitus; materiaalien kierrätys', labelEn: 'Waste collection, treatment and disposal activities' },
  { code: '39', label: 'Maaperän ja vesistöjen kunnostus ja muut ympäristönhuoltopalvelut', labelEn: 'Remediation activities and other waste management services' },
  { code: '41', label: 'Talonrakentaminen', labelEn: 'Construction of buildings' },
  { code: '42', label: 'Maa- ja vesirakentaminen', labelEn: 'Civil engineering' },
  { code: '43', label: 'Erikoistunut rakennustoiminta', labelEn: 'Specialised construction activities' },
  { code: '45', label: 'Moottoriajoneuvojen kauppa, huolto ja korjaus', labelEn: 'Wholesale and retail trade and repair of motor vehicles' },
  { code: '46', label: 'Tukkukauppa', labelEn: 'Wholesale trade, except of motor vehicles and motorcycles' },
  { code: '47', label: 'Vähittäiskauppa', labelEn: 'Retail trade, except of motor vehicles and motorcycles' },
  { code: '49', label: 'Maaliikenne ja putkijohtokuljetus', labelEn: 'Land transport and transport via pipelines' },
  { code: '50', label: 'Vesiliikenne', labelEn: 'Water transport' },
  { code: '51', label: 'Ilmaliikenne', labelEn: 'Air transport' },
  { code: '52', label: 'Varastointi ja liikennettä palveleva toiminta', labelEn: 'Warehousing and support activities for transportation' },
  { code: '53', label: 'Posti- ja kuriiritoiminta', labelEn: 'Postal and courier activities' },
  { code: '55', label: 'Majoitus', labelEn: 'Accommodation' },
  { code: '56', label: 'Ravitsemistoiminta', labelEn: 'Food and beverage service activities' },
  { code: '58', label: 'Kustannustoiminta', labelEn: 'Publishing activities' },
  { code: '59', label: 'Elokuva-, video- ja televisio-ohjelmatuotanto, äänitteiden ja musiikin kustantaminen', labelEn: 'Motion picture, video and television programme production' },
  { code: '60', label: 'Ohjelmatoiminta', labelEn: 'Programming and broadcasting activities' },
  { code: '61', label: 'Tietoliikenne', labelEn: 'Telecommunications' },
  { code: '62', label: 'Ohjelmistot, konsultointi ja siihen liittyvä toiminta', labelEn: 'Computer programming, consultancy and related activities' },
  { code: '63', label: 'Tietopalvelutoiminta', labelEn: 'Information service activities' },
  { code: '64', label: 'Rahoituspalvelut', labelEn: 'Financial service activities, except insurance and pension funding' },
  { code: '65', label: 'Vakuutus-, jälleenvakuutus- ja eläkevakuutustoiminta', labelEn: 'Insurance, reinsurance and pension funding' },
  { code: '66', label: 'Rahoitusta ja vakuuttamista palveleva toiminta', labelEn: 'Activities auxiliary to financial services and insurance activities' },
  { code: '68', label: 'Kiinteistöalan toiminta', labelEn: 'Real estate activities' },
  { code: '69', label: 'Lakiasiain- ja laskentatoimen palvelut', labelEn: 'Legal and accounting activities' },
  { code: '70', label: 'Pääkonttorien toiminta; liikkeenjohdon konsultointi', labelEn: 'Activities of head offices; management consultancy activities' },
  { code: '71', label: 'Arkkitehti- ja insinööripalvelut; tekninen testaus ja analysointi', labelEn: 'Architectural and engineering activities; technical testing' },
  { code: '72', label: 'Tieteellinen tutkimus ja kehittäminen', labelEn: 'Scientific research and development' },
  { code: '73', label: 'Mainostoiminta ja markkinatutkimus', labelEn: 'Advertising and market research' },
  { code: '74', label: 'Muut erikoistuneet palvelut liike-elämälle', labelEn: 'Other professional, scientific and technical activities' },
  { code: '75', label: 'Eläinlääkintäpalvelut', labelEn: 'Veterinary activities' },
  { code: '77', label: 'Vuokraus- ja leasingtoiminta', labelEn: 'Rental and leasing activities' },
  { code: '78', label: 'Työllistämistoiminta', labelEn: 'Employment activities' },
  { code: '79', label: 'Matkatoimistojen ja matkanjärjestäjien toiminta; varauspalvelut', labelEn: 'Travel agency, tour operator reservation service' },
  { code: '80', label: 'Turvallisuus-, vartiointi- ja etsiväpalvelut', labelEn: 'Security and investigation activities' },
  { code: '81', label: 'Kiinteistön- ja maisemanhoito', labelEn: 'Services to buildings and landscape activities' },
  { code: '82', label: 'Hallinto- ja tukipalvelut liike-elämälle', labelEn: 'Office administrative, office support and other business support' },
  { code: '84', label: 'Julkinen hallinto ja maanpuolustus; pakollinen sosiaalivakuutus', labelEn: 'Public administration and defence; compulsory social security' },
  { code: '85', label: 'Koulutus', labelEn: 'Education' },
  { code: '86', label: 'Terveyspalvelut', labelEn: 'Human health activities' },
  { code: '87', label: 'Sosiaalihuollon laitospalvelut', labelEn: 'Residential care activities' },
  { code: '88', label: 'Sosiaalihuollon avopalvelut', labelEn: 'Social work activities without accommodation' },
  { code: '90', label: 'Kulttuuri- ja viihdetoiminta', labelEn: 'Creative, arts and entertainment activities' },
  { code: '91', label: 'Kirjastojen, arkistojen, museoiden ja muiden kulttuurilaitosten toiminta', labelEn: 'Libraries, archives, museums and other cultural activities' },
  { code: '92', label: 'Rahapeli- ja vedonlyöntitoiminta', labelEn: 'Gambling and betting activities' },
  { code: '93', label: 'Urheilutoiminta sekä huvi- ja virkistyspalvelut', labelEn: 'Sports activities and amusement and recreation activities' },
  { code: '94', label: 'Järjestöjen toiminta', labelEn: 'Activities of membership organisations' },
  { code: '95', label: 'Tietokoneiden ja henkilökohtaisten ja kotitaloustavaroiden korjaus', labelEn: 'Repair of computers and personal and household goods' },
  { code: '96', label: 'Muut henkilökohtaiset palvelut', labelEn: 'Other personal service activities' },
  { code: '97', label: 'Kotitalouksien toiminta kotitaloustyöntekijöiden työnantajina', labelEn: 'Activities of households as employers of domestic personnel' },
  { code: '99', label: 'Kansainvälisten organisaatioiden ja toimielinten toiminta', labelEn: 'Activities of extraterritorial organisations and bodies' }
];

/**
 * Get category label by code
 */
export function getTOLLevel2Label(code) {
  const category = TOL_LEVEL_2_CATEGORIES.find(cat => cat.code === code);
  return category ? category.label : code;
}

/**
 * Get category label in English
 */
export function getTOLLevel2LabelEn(code) {
  const category = TOL_LEVEL_2_CATEGORIES.find(cat => cat.code === code);
  return category ? category.labelEn : code;
}

/**
 * Get Level 5 subcategory label by code
 */
export function getTOLLevel5Label(code) {
  const category = TOL_LEVEL_5_CATEGORIES.find(cat => cat.code === code);
  return category ? category.label : code;
}

/**
 * Get Level 5 subcategories for a specific Level 2 parent category
 */
export function getTOLLevel5ByLevel2(level2Code) {
  return TOL_LEVEL_5_CATEGORIES.filter(cat => cat.level2 === level2Code);
}

/**
 * Group companies by TOL Level 2 category
 * Used for displaying category counts in filters
 */
export function groupByTOLCategory(companies) {
  const groups = {};
  
  companies.forEach(company => {
    const code = company.attributes?.publicData?.industryCodeLevel2;
    if (code) {
      if (!groups[code]) {
        groups[code] = {
          code,
          label: getTOLLevel2Label(code),
          labelEn: getTOLLevel2LabelEn(code),
          companies: []
        };
      }
      groups[code].companies.push(company);
    }
  });
  
  return Object.values(groups).sort((a, b) => a.label.localeCompare(b.label, 'fi'));
}

/**
 * Get popular/common categories for featured display
 */
export const POPULAR_TOL_CATEGORIES = [
  '41', // Construction of buildings
  '43', // Specialised construction activities
  '46', // Wholesale trade
  '47', // Retail trade
  '56', // Food and beverage service activities
  '62', // Computer programming, consultancy
  '68', // Real estate activities
  '69', // Legal and accounting activities
  '70', // Management consultancy
  '81', // Services to buildings and landscape
  '85', // Education
  '86', // Human health activities
  '93', // Sports activities and amusement
  '96', // Other personal service activities
];

/**
 * Check if a Level 5 code belongs to a specific Level 2 category
 * Level 5 codes start with their parent Level 2 code (first 2 digits)
 * Example: "02100" belongs to "02", "62010" belongs to "62"
 */
export function isLevel5InLevel2(level5Code, level2Code) {
  if (!level5Code || !level2Code) {
    return false;
  }
  // Level 5 code format: XXYXX where XX matches the level 2 code
  return level5Code.toString().startsWith(level2Code.toString());
}

/**
 * Get all Level 5 codes for a specific Level 2 category
 * Returns array of level5 codes that belong to the given level2
 */
export function getTOLLevel5CodesForLevel2(level2Code) {
  if (!level2Code) {
    return [];
  }
  return TOL_LEVEL_5_CATEGORIES
    .filter(cat => isLevel5InLevel2(cat.code, level2Code))
    .map(cat => cat.code);
}

/**
 * Get grouped Level 2 categories with Level 1 section headers
 * Returns array with section headers and category options for better UX
 */
export function getGroupedTOLLevel2Categories() {
  const grouped = [];
  
  TOL_LEVEL_1_SECTIONS.forEach(section => {
    // Add section header
    grouped.push({
      isHeader: true,
      code: section.code,
      label: `${section.code} ${section.label}`,
    });
    
    // Add categories under this section
    section.codes.forEach(code => {
      const category = TOL_LEVEL_2_CATEGORIES.find(cat => cat.code === code);
      if (category) {
        grouped.push({
          isHeader: false,
          code: category.code,
          label: `  ${category.code} ${category.label}`,
          labelEn: category.labelEn,
        });
      }
    });
  });
  
  return grouped;
}
